"use client";

import desktopMediaQuery from "@casino/ui/Games/desktopMediaQuery";
import { useMediaQuery } from "@workspace/frontend-ui";
import * as React from "react";

export type ViewTypes = "min" | "expanded" | "fullscreen";

const useViewState = () => {
  const [currentView, setCurrentView] = React.useState<ViewTypes>("min");
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const gameBoxRef = React.useRef<HTMLDivElement>(null);
  const prevView = React.useRef<ViewTypes>("min");
  const didPushHistory = React.useRef(false);
  const isDesktop = useMediaQuery(desktopMediaQuery);

  const leaveFullscreen = React.useCallback(async (fromPopState: boolean) => {
    if (document.fullscreenElement) {
      await document.exitFullscreen().catch(() => {
        /* already gone */
      });
    }
    setCurrentView(prevView.current);
    const shouldRewind = !fromPopState && didPushHistory.current;
    didPushHistory.current = false;
    if (shouldRewind) window.history.back();
  }, []);

  React.useEffect(() => {
    const handleFullscreenChange = () => {
      if (document.fullscreenElement) return;
      setCurrentView((view) =>
        view === "fullscreen" ? prevView.current : view,
      );
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  React.useEffect(() => {
    const handlePopState = () => {
      if (currentView !== "fullscreen") return;
      void leaveFullscreen(true);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [currentView, leaveFullscreen]);

  React.useEffect(() => {
    // Expanded is desktop-only — a rotate or resize would otherwise strand the player with no way out.
    if (isDesktop) return;
    if (prevView.current === "expanded") prevView.current = "min";
    setCurrentView((view) => (view === "expanded" ? "min" : view));
  }, [isDesktop]);

  React.useEffect(() => {
    if (currentView === "fullscreen") return;
    iframeRef.current?.scrollIntoView({ block: "nearest" });
  }, [currentView]);

  React.useEffect(() => {
    // Runs before the session resolves, so iframeRef doesn't exist yet — gameBoxRef always does.
    gameBoxRef.current?.scrollIntoView({ block: "nearest" });
  }, []);

  React.useEffect(() => {
    if (currentView !== "fullscreen") return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [currentView]);

  const toggleFullscreen = async () => {
    if (currentView === "fullscreen") {
      await leaveFullscreen(false);
      return;
    }
    prevView.current = currentView;
    setCurrentView("fullscreen");
    window.history.pushState({ gameFullscreen: true }, "");
    didPushHistory.current = true;
    // Rejects on iOS Safari, which has no Fullscreen API for iframes; GameArea's fixed overlay covers that.
    await iframeRef.current?.requestFullscreen().catch(() => {
      /* CSS overlay only */
    });
  };

  const toggleExpand = () => {
    if (!isDesktop) return;
    setCurrentView((view) => (view === "min" ? "expanded" : "min"));
  };

  return {
    view: currentView,
    isDesktop,
    isExpanded: currentView === "expanded",
    isFullscreen: currentView === "fullscreen",
    toggleExpand,
    toggleFullscreen,
    iframeRef,
    gameBoxRef,
  };
};

export default useViewState;
